<script setup>
// FLOW-06 (SPEC S5 e a emenda do Gate 2; Tasks 6.1 e 6.4) — a página da seção Flows da aba PowerFlow,
// a primeira da aba: busca a lista de flows da conta (`GET /powerflow/flows`) e a entrega à lista
// aprovada no mockup (`FlowsDaConta`), que só mostra.
//
// **O que cada um vê** segue o servidor: "Novo flow" e "Abrir no editor" são de quem monta flow (o
// `pode_editar` da mesma resposta: o super-admin, ou o gestor com a chave POWERFLOW_ADMIN_MONTA_FLOW),
// e "Executar agora" é de quem vê a aba. O "Abrir no editor" leva ao rascunho mais novo, quando há
// (é o que o editor abriria a partir da publicada), e senão à publicada.
//
// **Sem a lista** (o PowerTools fora do ar responde 503 a ela inteira, porque o resumo vem dele), a
// página diz que não carregou e oferece tentar de novo, em vez da lista vazia.
//
// **Executar agora** (SPEC S2 e S5; Task 6.5) abre o diálogo aprovado (`ExecutarAgora`) com os params
// da última execução deste flow pela aba (decisão do Murilo, 25/10): a primeira da lista filtrada pelo
// nome e pelo invocador `manual`, e o detalhe dela, que traz os `params` (redigidos: o componente deixa
// vazio o campo do segredo). Só entram os params que a publicada ainda pede; sem execução anterior, ou
// se a busca falhar, o formulário abre vazio. Confirmar roda a publicada que o diálogo mostra e abre a
// execução nova em Execuções. A recusa de leigo do servidor (o 422 da R7, do schema, da conversa; o 404
// do flow que sumiu) volta ao diálogo, no campo que ela nomeia; a falha do PowerTools vai num alerta.
//
// Deploy do PowerFlow (D2, 16/10): quem abre o editor sem poder montar flow volta para cá (o
// `semChave` do editor, em `powerflow.routes.js`), e o motivo vai num alerta, como ia na página de
// Configurações › PowerTools. O endereço perde o pedido: recarregar a página não repete o aviso.
//
// **Os gatilhos** (FLOW-03a, SPEC S7; Task 7.3): a lista deles (`GET /powerflow/gatilhos`) vem junto,
// só os da versão publicada, um por flow (FLOW-03b). A falha dela não derruba a página: a linha só não
// fala de gatilho. Ligar e pausar mudam na hora e voltam atrás se o servidor recusar, com a frase
// dele (o 422 `gatilho_so_do_rascunho`); depois, o flow é relido, para a próxima batida da agenda.
// O selo do gatilho abre o detalhe (`powerflow_gatilho`).
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMontaFlow } from 'dashboard/composables/useMontaFlow';
import powerflowFlowsApi from 'dashboard/api/powerflowFlows';
import powerflowExecucoesApi from 'dashboard/api/powerflowExecucoes';
import powerflowGatilhosApi from 'dashboard/api/powerflowGatilhos';
import ExecutarAgora from './ExecutarAgora.vue';
import FlowsDaConta from './FlowsDaConta.vue';
import PainelDeFalha from './PainelDeFalha.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { montaFlow } = useMontaFlow();

const accountId = computed(() => Number(route.params.accountId));

const flows = ref([]);
const podeEditar = ref(false);
const carregando = ref(true);
const falhou = ref(false);

async function carregar() {
  carregando.value = true;
  falhou.value = false;
  try {
    const resposta = await powerflowFlowsApi.index(accountId.value);
    flows.value = resposta.flows;
    podeEditar.value = resposta.podeEditar;
  } catch {
    falhou.value = true;
  } finally {
    carregando.value = false;
  }
}

// -- Gatilhos
const gatilhos = ref(null);

// FLOW-03b (P14): um gatilho por flow, o da versão publicada. O flow sem nenhum é de Agente.
const gatilhoPorFlow = computed(() => {
  if (!gatilhos.value) return null;
  return gatilhos.value
    .filter(gatilho => gatilho.publicado)
    .reduce((porFlow, gatilho) => {
      porFlow[gatilho.flow_name] ||= gatilho;
      return porFlow;
    }, {});
});

async function carregarGatilhos(flowName) {
  try {
    const lidos = await powerflowGatilhosApi.index(accountId.value, {
      flowName,
    });
    gatilhos.value = flowName
      ? [
          ...(gatilhos.value || []).filter(g => g.flow_name !== flowName),
          ...lidos,
        ]
      : lidos;
  } catch {
    if (!flowName) gatilhos.value = null;
  }
}

const mensagemDoServidor = erro => {
  const recusa = erro?.response?.data?.erro;
  return recusa
    ? [recusa.mensagem, recusa.como_resolver].filter(Boolean).join(' ')
    : null;
};

async function alternarGatilho(gatilho, ligado) {
  const linha = gatilhos.value.find(g => g.id === gatilho.id);
  linha.ligado = ligado;
  try {
    const salvo = await powerflowGatilhosApi.alternar(
      accountId.value,
      gatilho.id,
      ligado
    );
    linha.ligado_em = salvo.ligado_em;
    await carregarGatilhos(gatilho.flow_name);
  } catch (erro) {
    linha.ligado = !ligado;
    useAlert(
      mensagemDoServidor(erro) ||
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.TOGGLE_ERROR')
    );
  }
}

const abrirGatilho = gatilho =>
  router.push({
    name: 'powerflow_gatilho',
    params: {
      accountId: accountId.value,
      nome: gatilho.flow_name,
      gatilhoId: gatilho.gatilho_id,
    },
  });

function novo() {
  router.push({
    name: 'powerflow_flow_novo',
    params: { accountId: accountId.value },
  });
}

function abrir(flow) {
  const entrada = flow.rascunho || flow.publicada;
  if (!entrada) return;
  router.push({
    name: 'powerflow_flow_editor',
    params: { accountId: accountId.value, id: entrada.entrada_id },
  });
}

// -- Executar agora
const dialogo = ref(null);
const aExecutar = ref(null);
const paramsIniciais = ref({});
const preparando = ref(false);
const executando = ref(false);
const recusa = ref(null);

// Os params da última execução deste flow pela aba, só os que a publicada ainda pede.
async function paramsDaUltimaExecucao(flow) {
  const { execucoes } = await powerflowExecucoesApi.index(accountId.value, {
    flow_nome: flow.nome,
    invocador: 'manual',
  });
  if (!execucoes.length) return {};
  const { params } = await powerflowExecucoesApi.show(
    accountId.value,
    execucoes[0].execucao_id
  );
  const pedidos = Object.keys(flow.publicada?.args_schema?.properties || {});
  return Object.fromEntries(
    Object.entries(params || {}).filter(([nome]) => pedidos.includes(nome))
  );
}

async function prepararExecucao(flow) {
  if (preparando.value || executando.value) return;
  preparando.value = true;
  let iniciais = {};
  try {
    iniciais = await paramsDaUltimaExecucao(flow);
  } catch {
    // Sem a última execução, o formulário abre vazio: ela só poupa digitação.
  }
  aExecutar.value = flow;
  paramsIniciais.value = iniciais;
  recusa.value = null;
  preparando.value = false;
  await nextTick();
  dialogo.value?.abrir();
}

async function executar(pedido) {
  const flow = aExecutar.value;
  executando.value = true;
  recusa.value = null;
  try {
    const { execucao_id: execucaoId } = await powerflowExecucoesApi.executar(
      accountId.value,
      {
        flow: flow.publicada.entrada_id,
        versao: flow.publicada.versao,
        ...pedido,
      }
    );
    dialogo.value?.fechar();
    router.push({
      name: 'powerflow_execucao',
      params: { accountId: accountId.value, execucaoId },
    });
  } catch (erro) {
    const corpo = erro?.response?.data || {};
    if (corpo.erro) recusa.value = corpo.erro;
    else {
      useAlert(corpo.mensagem || t('POWER_TOOLS.POWERFLOW.EXECUTE_NOW.ERROR'));
    }
  } finally {
    executando.value = false;
  }
}

function avisarQueSoAEquipeMonta() {
  if (montaFlow.value || route.query.aviso !== 'montar_flow') return;
  useAlert(t('POWER_TOOLS.FLOW_EDIT_COMING_SOON'));
  const { aviso, ...resto } = route.query;
  router.replace({ query: resto });
}

onMounted(() => {
  avisarQueSoAEquipeMonta();
  carregar();
  carregarGatilhos();
});
</script>

<template>
  <div class="flex-1 min-w-0 px-6 pt-4 pb-8 overflow-y-auto">
    <section v-if="falhou" class="flex flex-col max-w-6xl gap-4">
      <header class="flex flex-col gap-1 max-w-3xl">
        <h2 class="mb-0 text-heading-1 text-n-slate-12">
          {{ t('POWER_TOOLS.POWERFLOW.FLOWS.TITLE') }}
        </h2>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('POWER_TOOLS.POWERFLOW.FLOWS.DESCRIPTION') }}
        </p>
      </header>
      <PainelDeFalha
        :mensagem="t('POWER_TOOLS.POWERFLOW.FLOWS.LOAD_ERROR')"
        @tentar="carregar"
      />
    </section>
    <FlowsDaConta
      v-else
      class="max-w-6xl"
      :flows="flows"
      :pode-editar="podeEditar"
      :carregando="carregando"
      :gatilho-por-flow="gatilhoPorFlow"
      @novo="novo"
      @alternar-gatilho="alternarGatilho"
      @abrir-gatilho="abrirGatilho"
      @abrir="abrir"
      @executar="prepararExecucao"
    />
    <ExecutarAgora
      v-if="aExecutar"
      ref="dialogo"
      :flow="aExecutar"
      :params-iniciais="paramsIniciais"
      :executando="executando"
      :recusa="recusa"
      @executar="executar"
    />
  </div>
</template>
